import { Block } from '@components/core-ui/Block';
import { Select } from '@components/core-ui/form-elements/Select';
import { useAPI } from '@providers/useAPI';
import { useQueryParams } from '@providers/useQueryParams';
import { useAuthContext } from '@providers/AuthProvider';
import { useQuery } from '@tanstack/react-query';
import { PrismaRepositoryDto } from 'app/API';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { createFetchAllOrganizationRepositoriesQueryFn } from 'app/queries/repositories/createFetchAllOrganizationRepositoriesQueryFn';
import { repositoriesKeys } from 'app/queries/repositories/repositoriesKeys';
import { useEffect, useMemo } from 'react';

import { usePageContext } from '../../../providers/PageProvider';

type RepositoryItem = {
  label: string;
  value: string;
};

const NO_CASE_OPTION: RepositoryItem = {
  value: '',
  label: 'No case selected',
};

export const RepositoryBlock = () => {
  const { form, setFormItemValue, isLoading } = usePageContext();
  const { call } = useAPI();
  const { organization } = useAuthContext();
  const searchParams = useQueryParams();
  const organizationId = organization?.id ?? '';

  const repositoriesQuery = useQuery({
    queryKey: repositoriesKeys.allByOrg(organizationId),
    queryFn: createFetchAllOrganizationRepositoriesQueryFn(call),
    enabled: organizationId !== '',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  useNotifyQueryError(repositoriesQuery.error);

  const repositories = repositoriesQuery.data;
  const repositoryList = useMemo<RepositoryItem[]>(
    () =>
      repositories
        ? [
            NO_CASE_OPTION,
            ...repositories.map((item: PrismaRepositoryDto) => ({
              value: item.id,
              label: item.name,
            })),
          ]
        : [],
    [repositories],
  );

  const handleSelectChange = async (item: RepositoryItem) => {
    setFormItemValue('repositoryId', item?.value === 'null' ? null : item?.value);
  };

  useEffect(() => {
    if (!repositories?.length) {
      return;
    }

    const queryStringRepo = searchParams.get('repo');

    if (queryStringRepo) {
      const match = repositories.find((r: PrismaRepositoryDto) => r.slug === queryStringRepo);
      if (match) {
        handleSelectChange({ value: match.id, label: 'repositoryId' });
      }
    } else {
      if (form.item?.repositoryId) {
        const match = repositories.find((r: PrismaRepositoryDto) => r.id === form.item?.repositoryId);
        if (match) {
          handleSelectChange({ value: match.id, label: 'repositoryId' });
        }
      }
    }
  }, [repositories, form.item?.repositoryId]);

  return (
    <Block title="Case" description={<p>Associate this job with a case (optional)</p>}>
      <Select
        name="repositoryId"
        label="Case name"
        value={form.item?.repositoryId}
        disabled={isLoading}
        onChange={(label, value) => handleSelectChange({ label, value })}
        options={repositoryList}
        required={false}
      />
    </Block>
  );
};
